/**
 * @file queue.component.tsx
 * @description Основной компонент очереди для отображения и выбора элементов из списка в Task Manager приложении. Предоставляет интерфейс для просмотра, поиска и фильтрации данных с возможностью выбора записей из табличного представления. Поддерживает фильтрацию с сохранением пользовательских настроек, обработку ошибок API, модальные окна для просмотра/редактирования объектов и интеграцию с системой поиска по тексту.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-09-22
 * @dependencies ['@features/page-modal', '@features/catalog/components', '@hooks/use-modals', 'react', '@components/ui', '@components/ui/indicators/error-list', 'axios', '@utils/table-view', '@src/api/actions/queue', '@features/table', '@features/filter', '@src/types', '@features/object-form', '@utils/form-object-helper', '@features/catalog/context/search-text-context', '@utils/error-helper']
 * @related-files ['src/features/queue/queue.provider.tsx', 'src/features/queue/index.js', 'src/api/actions/queue.ts', 'src/features/catalog/components/header.component.tsx', 'src/features/table']
 */

import { PageModal } from '@components/ui/layout/page-modal';
import { Header } from '@features/catalog/components';
import { useModalQueue } from '@hooks/use-modals';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Loader } from '@components/ui';
import { ErrorList, ErrorListHandle } from '@components/ui/indicators/error-list';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { getDefaultCell } from '@utils/table-view';
import { QueueService } from '@src/api/actions/queue';
import { Table } from '@features/table';
import { FilterModal } from '@features/filter';
import { ToolbarTags as FilterTagLine } from '@features/filter';
import {
    getActiveFilters,
    getInitialValuesFromFilterControls,
    prepareFilterControlsToFetch,
    prepareFilterFormToFetch
} from '@features/filter';
import { useSavedFilters } from '@features/filter';
import { ITableItemDTO, TBaseObjectDto } from '@src/types';
import { ObjectForm } from '@features/object-form';
import { getObjectEntity } from '@src/utils/form-object-helper';
import { useSearchText } from '@features/catalog/context/search-text-context';
import { setErrors } from '@utils/error-helper';

interface IQueue {
    container?: HTMLDivElement;
    type: string;
    currentItems?: ITableItemDTO[];
    onRowClick?: (row: ITableItemDTO, column?: string) => void;
    stepsCount?: number;
    onClose?: Function;
}

const defaultQueueColumns = [
    {
        id: '$item',
        header: [{ text: 'Наименование' }],
        render: (cellValue: any) => getDefaultCell(cellValue)
    }
];

export const Queue = ({
    container,
    type,
    currentItems: _currentItems = [],
    onRowClick,
    stepsCount: _stepsCount = 1,
    onClose
}: IQueue) => {
    const { getFilters, saveFilters } = useSavedFilters(type);
    const { searchText } = useSearchText();
    const [entity, setEntity] = useState<TBaseObjectDto | undefined>(undefined);
    const [rows, setRows] = useState<ITableItemDTO[]>([]);
    const [isShow, setIsShow] = useState<boolean>(true);
    const [filterControls, setFilterControls] = useState<any>([]);
    const [isShowFilterModal, setIsShowFilterModal] = useState<boolean>(false);
    const errorListRef = useRef<ErrorListHandle | null>(null);
    const rowsRef = useRef<ITableItemDTO[]>(rows || []);
    const tableRef = useRef<any>(null);
    const lastOpenItem = useRef<any>(null);
    const scrollPositionRef = useRef<any>(null);
    const filterControlsRef = useRef<any>(filterControls);
    const activeFilters = useMemo(() => getActiveFilters(filterControls), [filterControls]);
    const [openedObject, setOpenedObject] = useState<TBaseObjectDto | null>(null);
    const objectEntityRef = useRef<any>(null);

    const closePageHandler = useModalQueue(() => {
        setIsShow(false);
        onClose && onClose();
    }, type);
    const [modalContainer, setModalContainer] = useState<HTMLDivElement | undefined>(container);

    useEffect(() => {
        async function tryGetEntity() {
            try {
                const entityData = await getObjectEntity(type);
                setEntity(entityData);
            } catch (error) {
                console.error('Ошибка получения entity:', error);
            }
        }
        tryGetEntity();
    }, [type]);

    useEffect(() => {
        if (!entity) {
            return;
        }

        async function tryFecthSavedFilters() {
            const savedFilters: TBaseObjectDto = await getFilters();
            const newFilterControls = entity?.getFilterControls ? entity.getFilterControls(savedFilters) : [];
            setFilterControls(newFilterControls);
            fetchData(savedFilters, newFilterControls);
        }

        setRows([]);
        tryFecthSavedFilters();
    }, [entity]);

    useEffect(() => {
        rowsRef.current = rows;
    }, [rows]);

    useEffect(() => {
        filterControlsRef.current = filterControls;
    }, [filterControls]);

    const fetchData = async (
        filterItems: TBaseObjectDto | null = null,
        _filterControls: any = filterControlsRef.current
    ) => {
        const prevItems = rowsRef.current;
        setRows([]);
        const fetchFilters = filterItems
            ? prepareFilterFormToFetch(filterItems, _filterControls)
            : prepareFilterControlsToFetch(_filterControls);
        const response: AxiosResponse | AxiosError = await QueueService.getList(type, {
            searchText,
            filterItems: fetchFilters
        });

        if (axios.isAxiosError(response)) {
            setRows(prevItems);
            setErrors(response, errorListRef.current);
            return;
        }

        saveFilters(filterControlsRef.current);
        setRows(response.data);
        tableRef.current?.scroll(0, 0);
        setIsShow(true);
    };

    useEffect(() => {
        setModalContainer(container || (document.getElementById('MainLayout') as HTMLDivElement));

        return () => {
            closePageHandler();
        };
    }, []);

    const onTableDataUpdated = () => {
        if (lastOpenItem.current && scrollPositionRef.current) {
            setTimeout(() => {
                tableRef.current?.selectRow(lastOpenItem.current.id);
                tableRef.current?.scroll(scrollPositionRef.current.x, scrollPositionRef.current.y);
                lastOpenItem.current = null;
                scrollPositionRef.current = null;
            }, 10);
        }
    };

    if (!modalContainer) {
        return;
    }

    return (
        <PageModal container={modalContainer} closePageHandler={closePageHandler} isOpen={isShow}>
            <div className="queue gap-default h-full w-full relative">
                <div className="queue__wrapper absolute w-full h-full flex flex-col pr-[3.1756vmin]">
                    <Header
                        title="Выбор из списка"
                        closePageHandler={closePageHandler}
                        filterControls={filterControls}
                        onFilterButtonClick={() => setIsShowFilterModal(true)}
                        isShowFilterModal={isShowFilterModal}
                        isCanShowModal={!isShowFilterModal}
                    />

                    {activeFilters.length > 0 && (
                        <FilterTagLine
                            filters={activeFilters}
                            onFilterClick={() => setIsShowFilterModal(true)}
                            onClearFilters={(filterName: string | undefined) => {
                                let newFilterControls = null;

                                if (filterName) {
                                    newFilterControls = filterControls.map((filterControl: any) => {
                                        if (filterControl.name !== filterName) {
                                            return filterControl;
                                        }

                                        return { ...filterControl, value: null };
                                    });
                                } else {
                                    newFilterControls = entity?.getFilterControls() || [];
                                }

                                setFilterControls(newFilterControls);
                                fetchData(getInitialValuesFromFilterControls(newFilterControls), newFilterControls);
                            }}
                        />
                    )}

                    {!!rows.length ? (
                        <Table
                            items={rows}
                            columns={entity?.columns || defaultQueueColumns}
                            selection="row"
                            className="clickable-rows"
                            ref={tableRef}
                            onTableDataUpdated={onTableDataUpdated}
                            onRowClick={(row: ITableItemDTO) => {
                                if (onRowClick) {
                                    onRowClick(row);
                                } else if (row.$item.$type) {
                                    lastOpenItem.current = row;
                                    scrollPositionRef.current = tableRef.current?.getScroll() || { x: '0', y: '0' };
                                    setOpenedObject(row.$item);
                                    objectEntityRef.current = getObjectEntity(row.$item.$type);
                                }
                            }}
                        />
                    ) : (
                        <Loader />
                    )}
                </div>

                {Boolean(openedObject) && objectEntityRef.current && (
                    <ObjectForm
                        type={openedObject?.$type}
                        initialItem={openedObject}
                        onClose={() => {
                            fetchData();
                            setOpenedObject(null);
                            objectEntityRef.current = null;
                        }}
                        {...objectEntityRef.current}
                    />
                )}

                <ErrorList ref={errorListRef} />

                {isShowFilterModal && (
                    <FilterModal
                        container={modalContainer}
                        isShow={isShowFilterModal}
                        setIsShow={setIsShowFilterModal}
                        filterControls={filterControls}
                        applyNewFilterControls={setFilterControls}
                        fetchDataHadler={fetchData}
                        itemType={type}
                    />
                )}
            </div>
        </PageModal>
    );
};
